import React, { useEffect, useMemo, useState } from "react";
import { canSubmitDoc } from "@/lib/submitPerms";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FormattedNumberInput from "@/components/FormattedNumberInput";
import {
  PlusIcon, TrashIcon, CaretDownIcon, CaretRightIcon,
  CheckCircleIcon, PaperPlaneTiltIcon, LockIcon, ArrowClockwiseIcon, PrinterIcon,
} from "@phosphor-icons/react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell,
} from "recharts";

/*
 Rejection Entry (Feb 2026)
 --------------------------
 One-page module: KPI tiles + list + full-form dialog. Dialog uses
 collapsible sections so the header + entry qtys stay on-screen while
 root-cause / defect-details expand only when the operator needs them.
 Live client-side previews of the four validation rules keep the flow
 quick; the backend re-runs every rule on Submit / Approve.
*/

const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

const SIX_M = ["Man", "Machine", "Material", "Method", "Measurement", "Environment"];
const DISPOSITIONS = ["Rework", "Scrap", "Downgrade", "Hold", "Return to Process"];
const SEVERITIES = ["Minor", "Major", "Critical"];
const STATUS_TONE = {
  Draft: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  Submitted: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Closed: "bg-blue-500/15 text-blue-300 border-blue-500/30",
};

function Section({ title, defaultOpen = true, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[var(--border)] rounded-sm bg-[var(--bg-0)]/40" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white"
      >
        <span className="flex items-center gap-2">
          {open ? <CaretDownIcon size={12} weight="bold" /> : <CaretRightIcon size={12} weight="bold" />}
          {title}
        </span>
      </button>
      {open && <div className="px-3 pb-3 pt-1">{children}</div>}
    </div>
  );
}

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1 ${className}`}>
    <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
    {children}
  </div>
);

const RO = ({ label, value, testid }) => (
  <div className="space-y-1">
    <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
    <div data-testid={testid} className="h-9 px-3 flex items-center bg-[var(--bg-2)]/40 border border-[var(--border)] rounded-sm text-white text-sm font-mono">
      {value ?? "—"}
    </div>
  </div>
);

const emptyDetail = () => ({
  category: "", defect: "", severity: "Minor",
  defect_qty: 0, unit: "meter", roll_no: "", location: "", remarks: "",
});

const emptyEntry = () => ({
  rejection_no: "", date: new Date().toISOString().slice(0, 10),
  shift: "A", department: "",
  plan_id: "", order_id: "", machine_id: "", operator_id: "", supervisor_id: "",
  customer_id: "", item_id: "", item_code: "", item_description: "",
  lot_no: "", batch_no: "",
  planned_qty: 0, produced_qty: 0, inspected_qty: 0,
  rejected_qty: 0, rework_qty: 0, scrap_qty: 0, rejection_pct: 0,
  defect_details: [emptyDetail()],
  root_cause: {
    category_6m: "", root_cause: "", immediate_action: "",
    corrective_action: "", preventive_action: "", responsible: "", target_date: "",
  },
  disposition: "",
  status: "Draft",
});

export default function Rejections() {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [defects, setDefects] = useState([]);
  const [dash, setDash] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEntry());
  const [editId, setEditId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    const [r, pl, o, it, m, op, dp, c, df, ds] = await Promise.all([
      api.get("/rejections/"),
      api.get("/production-plans/"),
      api.get("/sales-orders/"),
      api.get("/items/"),
      api.get("/machines/"),
      api.get("/operators/"),
      api.get("/departments/"),
      api.get("/customers/"),
      api.get("/defects/"),
      api.get("/rejections/dashboard"),
    ]);
    setRows(r.data); setPlans(pl.data); setOrders(o.data); setItems(it.data);
    setMachines(m.data); setOperators(op.data); setDepartments(dp.data);
    setCustomers(c.data); setDefects(df.data); setDash(ds.data);
  };
  useEffect(() => { load(); }, []);

  // Machines filtered by selected department (falls back to full list when blank
  // or when no machine actually carries that department — some legacy machines
  // have empty department strings). Feb 2026.
  const filteredMachines = useMemo(() => {
    if (!form.department) return machines;
    const hits = machines.filter(m => (m.department || "") === form.department);
    return hits.length > 0 ? hits : machines;
  }, [form.department, machines]);

  // Distinct defect categories — sourced from the existing Defects Master's
  // `category` field. When admins haven't classified defects yet, fall back
  // to "General" so the grid stays usable.
  const defectCategories = useMemo(() => {
    const set = new Set();
    defects.forEach(d => { if (d.category) set.add(d.category); });
    return set.size ? Array.from(set).sort() : ["General"];
  }, [defects]);

  const defectsInCategory = (cat) =>
    defects.filter(d => (!cat || (d.category || "General") === cat));

  // ── Header auto-fill from Work Order (source SO/ESO snapshot) ──
  const onPlanChange = (planId) => {
    const p = plans.find(x => x.id === planId);
    if (!p) { setForm(f => ({ ...f, plan_id: planId })); return; }
    const it = items.find(i => i.id === p.item_id) || {};
    const o = orders.find(x => x.id === p.order_id) || {};
    // Sum every production entry for this plan to derive Produced Qty.
    // Server already keeps `actual_qty` in sync — trust it.
    const producedQty = +(p.actual_qty || 0);
    setForm(f => ({
      ...f,
      plan_id: planId,
      order_id: p.order_id || "",
      machine_id: p.machine_id || "",
      item_id: p.item_id || "",
      item_code: it.code || "",
      item_description: it.description || "",
      customer_id: o.customer_id || "",
      planned_qty: +(p.planned_qty || 0),
      produced_qty: producedQty,
      inspected_qty: f.inspected_qty || producedQty,   // sensible default
    }));
    toast.success(`Loaded ${o.order_no || "order"} · ${it.code || "item"}`);
  };

  // Live derived values (also computed server-side on save).
  const totalDefectQty = useMemo(() =>
    (form.defect_details || []).reduce((s, d) => s + (+d.defect_qty || 0), 0)
  , [form.defect_details]);

  const rejectionPct = useMemo(() => {
    const insp = +form.inspected_qty || 0;
    if (!insp) return 0;
    return Math.round((((+form.rejected_qty || 0) / insp) * 100) * 100) / 100;
  }, [form.rejected_qty, form.inspected_qty]);

  // Validation preview for the footer chip.
  const validation = useMemo(() => {
    const rej = +form.rejected_qty || 0;
    const insp = +form.inspected_qty || 0;
    const rework = +form.rework_qty || 0;
    const scrap = +form.scrap_qty || 0;
    const problems = [];
    if (!form.plan_id) problems.push("Pick a Work Order");
    if (!form.machine_id) problems.push("Pick a Machine");
    if (!form.operator_id) problems.push("Pick an Operator");
    if (rej <= 0) problems.push("Rejected Qty must be > 0");
    if (insp > 0 && rej > insp) problems.push(`Rejected (${rej}) exceeds Inspected (${insp})`);
    if (Math.abs((rework + scrap) - rej) > 0.01) problems.push(`Rework + Scrap must equal Rejected (${rej})`);
    if (!(form.defect_details || []).length) problems.push("Add at least one Defect");
    if (Math.abs(totalDefectQty - rej) > 0.01) problems.push(`Sum of Defect Qty (${totalDefectQty}) must equal Rejected Qty (${rej})`);
    return problems;
  }, [form, totalDefectQty]);

  // Defect grid mutators
  const addDetail = () => setForm(f => ({ ...f, defect_details: [...(f.defect_details || []), emptyDetail()] }));
  const removeDetail = (i) => setForm(f => ({ ...f, defect_details: f.defect_details.filter((_, idx) => idx !== i) }));
  const patchDetail = (i, patch) => setForm(f => ({
    ...f,
    defect_details: f.defect_details.map((d, idx) => idx === i ? { ...d, ...patch } : d),
  }));

  const openNew = () => {
    setEditId(null); setForm(emptyEntry()); setOpen(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({ ...emptyEntry(), ...row, root_cause: { ...emptyEntry().root_cause, ...(row.root_cause || {}) } });
    setOpen(true);
  };

  const save = async (nextStatus = "Draft") => {
    try {
      const payload = { ...form, status: nextStatus, rejection_pct: rejectionPct };
      const res = editId
        ? await api.put(`/rejections/${editId}`, payload)
        : await api.post("/rejections/", payload);
      toast.success(editId ? `Updated ${res.data.rejection_no || ""}` : `Created ${res.data.rejection_no || ""}`);
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const transition = async (row, action) => {
    try {
      const res = await api.post(`/rejections/${row.id}/${action}`);
      toast.success(`${row.rejection_no} → ${res.data.status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.rejection_no}?`)) return;
    try {
      await api.delete(`/rejections/${row.id}`);
      toast.success("Deleted"); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // Rejection PDF — inline (opens in new tab). Uses a Blob so we can
  // pass the JWT via the axios instance, then hand a browser-openable
  // URL to `window.open`. Handles 401/403/404 with a toast.
  const printPdf = async (row) => {
    try {
      const res = await api.get(`/rejections/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // Revoke after 1 min so we don't leak object URLs.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF failed");
    }
  };

  const machineCode = (id) => machines.find(m => m.id === id)?.code || "—";
  const operatorName = (id) => operators.find(o => o.id === id)?.name || "—";
  const itemCode = (id) => items.find(i => i.id === id)?.code || "—";
  const orderNo = (id) => orders.find(o => o.id === id)?.order_no || "—";

  const filtered = useMemo(
    () => rows.filter(r => filterStatus === "all" || r.status === filterStatus),
    [rows, filterStatus]
  );

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Quality Transactions</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Rejection Entry</h1>
          <div className="text-xs text-zinc-500 mt-1">Log rejections against Work Orders · Draft → Submitted → Approved → Closed · defect analysis + CAPA + Pareto KPIs</div>
        </div>
        <button data-testid="new-rejection-btn" onClick={openNew} className="h-10 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
          <PlusIcon size={14} weight="bold" />New Rejection
        </button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rejection-kpi">
        <KpiTile label="Total Rejected" value={dash?.total_rejected_qty ?? 0} suffix="units" tone="red" testid="kpi-total" />
        <KpiTile label="Rejection %" value={dash?.rejection_pct ?? 0} suffix="%" tone="amber" testid="kpi-pct" />
        <KpiTile label="Rework %" value={dash?.rework_pct ?? 0} suffix="%" tone="blue" testid="kpi-rework" />
        <KpiTile label="Scrap %" value={dash?.scrap_pct ?? 0} suffix="%" tone="rose" testid="kpi-scrap" />
      </div>

      {/* Slice tables */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <ParetoCard title="Top Defects (Pareto)" data={dash?.top_defects} testid="slice-defects" />
        <SliceCard title="Machine-wise" data={dash?.by_machine} resolveKey={machineCode} testid="slice-machine" />
        <SliceCard title="Department-wise" data={dash?.by_department} resolveKey={x => x} testid="slice-dept" />
        <SliceCard title="Shift-wise" data={dash?.by_shift} resolveKey={x => x} testid="slice-shift" />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-zinc-500 font-mono uppercase tracking-widest">Filter:</span>
        {["all", "Draft", "Submitted", "Approved", "Closed"].map(s => (
          <button
            key={s}
            data-testid={`filter-${s}`}
            onClick={() => setFilterStatus(s)}
            className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${
              filterStatus === s
                ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white"
                : "border-[var(--border)] text-zinc-500 hover:text-white"
            }`}
          >
            {s === "all" ? `All (${rows.length})` : s}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3 font-medium">Rejection No</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Order</th>
              <th className="text-left px-4 py-3 font-medium">Item</th>
              <th className="text-left px-4 py-3 font-medium">Machine</th>
              <th className="text-left px-4 py-3 font-medium">Shift</th>
              <th className="text-right px-4 py-3 font-medium">Rejected</th>
              <th className="text-right px-4 py-3 font-medium">Rej %</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} data-testid={`row-${r.id}`} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                <td className="px-4 py-3 font-mono text-white">
                  <button onClick={() => openEdit(r)} className="hover:text-[var(--accent)]">{r.rejection_no}</button>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-400">{r.date}</td>
                <td className="px-4 py-3 font-mono text-zinc-400">{orderNo(r.order_id)}</td>
                <td className="px-4 py-3 font-mono text-zinc-400">{itemCode(r.item_id)}</td>
                <td className="px-4 py-3 font-mono text-zinc-400">{machineCode(r.machine_id)}</td>
                <td className="px-4 py-3 font-mono text-zinc-400">{r.shift || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-red-300">{r.rejected_qty}</td>
                <td className="px-4 py-3 text-right font-mono text-zinc-300">{r.rejection_pct}%</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${STATUS_TONE[r.status] || ""}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-right space-x-1">
                  <button title="Print PDF" data-testid={`print-${r.id}`} onClick={() => printPdf(r)} className="p-1.5 rounded-sm text-zinc-400 hover:text-white hover:bg-[var(--bg-2)]"><PrinterIcon size={14} /></button>
                  {r.status === "Draft" && canSubmitDoc("QA") && (
                    <button title="Submit" onClick={() => transition(r, "submit")} className="p-1.5 rounded-sm text-zinc-400 hover:text-amber-300 hover:bg-amber-500/10"><PaperPlaneTiltIcon size={14} /></button>
                  )}
                  {r.status === "Submitted" && (
                    <button title="Approve" onClick={() => transition(r, "approve")} className="p-1.5 rounded-sm text-zinc-400 hover:text-emerald-300 hover:bg-emerald-500/10"><CheckCircleIcon size={14} /></button>
                  )}
                  {r.status === "Approved" && (
                    <button title="Close" onClick={() => transition(r, "close")} className="p-1.5 rounded-sm text-zinc-400 hover:text-blue-300 hover:bg-blue-500/10"><LockIcon size={14} /></button>
                  )}
                  {(r.status === "Submitted" || r.status === "Approved") && (
                    <button title="Back to Draft" onClick={() => transition(r, "reject")} className="p-1.5 rounded-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-500/10"><ArrowClockwiseIcon size={14} /></button>
                  )}
                  {r.status === "Draft" && (
                    <button title="Delete" onClick={() => remove(r)} className="p-1.5 rounded-sm text-zinc-400 hover:text-red-400 hover:bg-red-500/10"><TrashIcon size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-16 text-zinc-500">No rejections {filterStatus !== "all" ? `in ${filterStatus}` : "yet"}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Full-form Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              {editId ? "Edit Rejection" : "New Rejection"}
              {form.rejection_no && <span className="text-xs font-mono text-zinc-400">{form.rejection_no}</span>}
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${STATUS_TONE[form.status] || ""}`}>{form.status}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Header */}
            <Section title="Header" testid="section-header">
              <div className="grid grid-cols-4 gap-3">
                <F label="Date"><input type="date" data-testid="rej-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={inp} /></F>
                <F label="Shift">
                  <Select value={form.shift} onValueChange={v => setForm({ ...form, shift: v })}>
                    <SelectTrigger data-testid="rej-shift" className={selCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                      {["A", "B", "C"].map(s => <SelectItem key={s} value={s}>Shift {s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Department">
                  <Select value={form.department} onValueChange={v => setForm({ ...form, department: v, machine_id: "" })}>
                    <SelectTrigger data-testid="rej-dept" className={selCls}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-56">
                      {departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Work Order *">
                  <Select value={form.plan_id} onValueChange={onPlanChange}>
                    <SelectTrigger data-testid="rej-plan" className={selCls}><SelectValue placeholder="Pick a Work Order" /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                      {plans.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {(orders.find(o => o.id === p.order_id)?.order_no || "SO")} · {(items.find(i => i.id === p.item_id)?.code || "—")} · {machines.find(m => m.id === p.machine_id)?.code || "—"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Machine *">
                  <Select value={form.machine_id} onValueChange={v => setForm({ ...form, machine_id: v })}>
                    <SelectTrigger data-testid="rej-machine" className={selCls}><SelectValue placeholder="Pick machine" /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                      {filteredMachines.map(m => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Operator *">
                  <Select value={form.operator_id} onValueChange={v => setForm({ ...form, operator_id: v })}>
                    <SelectTrigger data-testid="rej-operator" className={selCls}><SelectValue placeholder="Pick" /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                      {operators.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Supervisor">
                  <Select value={form.supervisor_id} onValueChange={v => setForm({ ...form, supervisor_id: v })}>
                    <SelectTrigger className={selCls}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                      {operators.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Lot No"><input value={form.lot_no} onChange={e => setForm({ ...form, lot_no: e.target.value })} className={inp} /></F>
                <F label="Batch No"><input value={form.batch_no} onChange={e => setForm({ ...form, batch_no: e.target.value })} className={inp} /></F>
                <RO label="Customer" value={customers.find(c => c.id === form.customer_id)?.name} testid="rej-customer-ro" />
                <RO label="Sales Order" value={orderNo(form.order_id)} testid="rej-order-ro" />
                <RO label="Item Code" value={form.item_code} testid="rej-item-code-ro" />
              </div>
              <div className="mt-3">
                <RO label="Item Description" value={form.item_description} testid="rej-item-desc-ro" />
              </div>
            </Section>

            {/* Production Details */}
            <Section title="Production Details (Read Only)" testid="section-prod">
              <div className="grid grid-cols-3 gap-3">
                <RO label="Planned Qty" value={form.planned_qty} testid="rej-planned" />
                <RO label="Produced Qty" value={form.produced_qty} testid="rej-produced" />
                <F label="Inspected Qty">
                  <FormattedNumberInput step="0.01" data-testid="rej-inspected" value={form.inspected_qty}
                    onChange={e => setForm({ ...form, inspected_qty: parseFloat(e.target.value) || 0 })} className={inp} />
                </F>
              </div>
            </Section>

            {/* Entry qtys */}
            <Section title="Rejection Qtys" testid="section-qtys">
              <div className="grid grid-cols-4 gap-3">
                <F label="Rejected Qty *">
                  <FormattedNumberInput step="0.01" data-testid="rej-rejected" value={form.rejected_qty}
                    onChange={e => setForm({ ...form, rejected_qty: parseFloat(e.target.value) || 0 })} className={inp} />
                </F>
                <F label="Rework Qty">
                  <FormattedNumberInput step="0.01" data-testid="rej-rework" value={form.rework_qty}
                    onChange={e => setForm({ ...form, rework_qty: parseFloat(e.target.value) || 0 })} className={inp} />
                </F>
                <F label="Scrap Qty">
                  <FormattedNumberInput step="0.01" data-testid="rej-scrap" value={form.scrap_qty}
                    onChange={e => setForm({ ...form, scrap_qty: parseFloat(e.target.value) || 0 })} className={inp} />
                </F>
                <RO label="Rejection %" value={`${rejectionPct}%`} testid="rej-pct-ro" />
              </div>
            </Section>

            {/* Defect Details */}
            <Section title={`Defect Details (${(form.defect_details || []).length})`} testid="section-defects">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left px-2 py-1">Category</th>
                      <th className="text-left px-2 py-1">Defect</th>
                      <th className="text-left px-2 py-1">Severity</th>
                      <th className="text-right px-2 py-1">Qty</th>
                      <th className="text-left px-2 py-1">Unit</th>
                      <th className="text-left px-2 py-1">Roll No</th>
                      <th className="text-left px-2 py-1">Location</th>
                      <th className="text-left px-2 py-1">Remarks</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.defect_details || []).map((d, i) => {
                      const opts = defectsInCategory(d.category);
                      return (
                        <tr key={i} data-testid={`defect-row-${i}`}>
                          <td className="px-1 py-1">
                            <Select value={d.category} onValueChange={v => patchDetail(i, { category: v, defect: "" })}>
                              <SelectTrigger className={`${selCls} h-8 text-xs`}><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-56">
                                {defectCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1">
                            <Select value={d.defect} onValueChange={v => patchDetail(i, { defect: v })}>
                              <SelectTrigger className={`${selCls} h-8 text-xs`}><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-56">
                                {opts.map(o => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1">
                            <Select value={d.severity} onValueChange={v => patchDetail(i, { severity: v })}>
                              <SelectTrigger className={`${selCls} h-8 text-xs`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                                {SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1">
                            <FormattedNumberInput step="0.01" value={d.defect_qty}
                              onChange={e => patchDetail(i, { defect_qty: parseFloat(e.target.value) || 0 })}
                              className={`${inp} h-8 text-right text-xs`} />
                          </td>
                          <td className="px-1 py-1">
                            <Select value={d.unit} onValueChange={v => patchDetail(i, { unit: v })}>
                              <SelectTrigger className={`${selCls} h-8 text-xs`}><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                                <SelectItem value="meter">meter</SelectItem>
                                <SelectItem value="kg">kg</SelectItem>
                                <SelectItem value="nos">nos</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1"><input value={d.roll_no || ""} onChange={e => patchDetail(i, { roll_no: e.target.value })} className={`${inp} h-8 text-xs`} /></td>
                          <td className="px-1 py-1"><input value={d.location || ""} onChange={e => patchDetail(i, { location: e.target.value })} className={`${inp} h-8 text-xs`} /></td>
                          <td className="px-1 py-1"><input value={d.remarks || ""} onChange={e => patchDetail(i, { remarks: e.target.value })} className={`${inp} h-8 text-xs`} /></td>
                          <td className="px-1 py-1">
                            <button onClick={() => removeDetail(i)} className="p-1.5 text-zinc-400 hover:text-red-400"><TrashIcon size={12} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <button data-testid="add-defect" onClick={addDetail} className="text-[11px] px-2.5 py-1 rounded-sm border border-[var(--border)] text-zinc-300 hover:text-white hover:border-[var(--accent)] flex items-center gap-1">
                  <PlusIcon size={11} weight="bold" />Add Defect Row
                </button>
                <div data-testid="defect-sum" className={`text-[11px] font-mono ${Math.abs(totalDefectQty - (+form.rejected_qty || 0)) < 0.01 ? "text-emerald-400" : "text-amber-400"}`}>
                  Σ Defect Qty = {totalDefectQty} · Rejected = {+form.rejected_qty || 0}
                </div>
              </div>
            </Section>

            {/* Root Cause (6M) */}
            <Section title="Root Cause / CAPA" defaultOpen={false} testid="section-root">
              <div className="grid grid-cols-3 gap-3">
                <F label="Root Cause Category (6M)">
                  <Select value={form.root_cause?.category_6m || ""} onValueChange={v => setForm({ ...form, root_cause: { ...form.root_cause, category_6m: v } })}>
                    <SelectTrigger data-testid="rc-6m" className={selCls}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                      {SIX_M.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Responsible Person"><input value={form.root_cause?.responsible || ""} onChange={e => setForm({ ...form, root_cause: { ...form.root_cause, responsible: e.target.value } })} className={inp} /></F>
                <F label="Target Date"><input type="date" value={form.root_cause?.target_date || ""} onChange={e => setForm({ ...form, root_cause: { ...form.root_cause, target_date: e.target.value } })} className={inp} /></F>
                <F label="Root Cause" className="col-span-3"><input value={form.root_cause?.root_cause || ""} onChange={e => setForm({ ...form, root_cause: { ...form.root_cause, root_cause: e.target.value } })} className={inp} /></F>
                <F label="Immediate Action"><input value={form.root_cause?.immediate_action || ""} onChange={e => setForm({ ...form, root_cause: { ...form.root_cause, immediate_action: e.target.value } })} className={inp} /></F>
                <F label="Corrective Action"><input value={form.root_cause?.corrective_action || ""} onChange={e => setForm({ ...form, root_cause: { ...form.root_cause, corrective_action: e.target.value } })} className={inp} /></F>
                <F label="Preventive Action"><input value={form.root_cause?.preventive_action || ""} onChange={e => setForm({ ...form, root_cause: { ...form.root_cause, preventive_action: e.target.value } })} className={inp} /></F>
              </div>
            </Section>

            {/* Disposition */}
            <Section title="Disposition" defaultOpen={false} testid="section-disp">
              <F label="Disposition">
                <Select value={form.disposition} onValueChange={v => setForm({ ...form, disposition: v })}>
                  <SelectTrigger data-testid="rej-disposition" className={selCls}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {DISPOSITIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            </Section>

            {/* Live validation summary */}
            {validation.length > 0 && (
              <div data-testid="validation-summary" className="rounded-sm border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200">
                <div className="font-mono uppercase tracking-widest text-amber-300 mb-1">Blockers before Submit</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {validation.map((v, i) => <li key={i}>{v}</li>)}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            {form.status === "Draft" && <button data-testid="save-draft" onClick={() => save("Draft")} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-200 hover:border-[var(--accent)]">Save Draft</button>}
            {form.status === "Draft" && canSubmitDoc("QA") && (<button
              data-testid="save-submit"
              disabled={validation.length > 0}
              onClick={() => save("Submitted")}
              className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-sm font-semibold text-white"
            >
              Save & Submit
            </button>)}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiTile({ label, value, suffix, tone, testid }) {
  const toneMap = {
    red: "border-red-500/30 bg-red-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
    blue: "border-blue-500/30 bg-blue-500/5",
    rose: "border-rose-500/30 bg-rose-500/5",
  };
  return (
    <div data-testid={testid} className={`tactical-card px-4 py-3 border ${toneMap[tone] || ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <div className="font-display text-2xl font-black text-white">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{suffix}</div>
      </div>
    </div>
  );
}

function SliceCard({ title, data, resolveKey, testid }) {
  const rows = (data || []).slice(0, 5);
  return (
    <div data-testid={testid} className="tactical-card px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-zinc-600">No data yet</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="truncate text-zinc-300">{resolveKey(r.key)}</span>
              <span className="font-mono text-white">{r.qty}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pareto: bars sorted desc by qty + cumulative-% overlay so the 80/20
// break-point is instantly obvious. Uses recharts ComposedChart. Kept small
// (h-40) so the card sits in the same row as the other slice tiles.
function ParetoCard({ title, data, testid }) {
  const chartData = useMemo(() => {
    const rows = (data || []).slice(0, 8);
    const total = rows.reduce((s, r) => s + (+r.qty || 0), 0) || 1;
    let running = 0;
    return rows.map((r) => {
      running += +r.qty || 0;
      return { name: r.key, qty: +r.qty || 0, cum: Math.round((running / total) * 100) };
    });
  }, [data]);
  return (
    <div data-testid={testid} className="tactical-card px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{title}</div>
      {chartData.length === 0 ? (
        <div className="text-xs text-zinc-600 h-32 flex items-center">No data yet</div>
      ) : (
        <div className="h-40 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#a1a1aa" }} interval={0} />
              <YAxis yAxisId="left" tick={{ fontSize: 9, fill: "#a1a1aa" }} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 9, fill: "#a1a1aa" }} unit="%" />
              <Tooltip contentStyle={{ background: "var(--bg-1)", border: "1px solid var(--border)", fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="qty" fill="#ef4444" radius={[2, 2, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? "#f87171" : "#ef4444"} />
                ))}
              </Bar>
              <Line yAxisId="right" type="monotone" dataKey="cum" stroke="#fbbf24" strokeWidth={2} dot={{ r: 3, fill: "#fbbf24" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
